import { ClientMsgKind } from '@infiltre/shared';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useGameStore, useSessionStore } from '@/shared/hooks/use-shallow-store';

const REACTIONS = [
  { emoji: '👀', label: 'sus', color: 'var(--color-civ)' },
  { emoji: '🤨', label: 'huh', color: 'var(--color-white-gold)' },
  { emoji: '😂', label: 'lol', color: 'var(--color-pop)' },
  { emoji: '🔥', label: 'fire', color: 'var(--color-und)' },
  { emoji: '💀', label: 'caught', color: 'var(--color-ink)' },
  { emoji: '🤐', label: 'noted', color: 'var(--color-civ-soft)' },
] as const;

const BURST_TTL_MS = 1500;

type Burst = {
  /** Unique id keyed off `(playerId, at)` so server echoes don't dup. */
  id: string;
  emoji: string;
  dx: number;
  dy: number;
  dur: number;
};

export type ReactionBarProps = {
  disabled?: boolean;
  /**
   * Fires on local tap so the parent can broadcast via the WS driver. When
   * omitted (e.g. standalone) the bar still bursts locally; the network
   * leg is opt-in. The component subscribes to `state.reactions` directly
   * so every player in an online room sees every other player's bursts.
   */
  onReact?: (emoji: string) => void;
};

/**
 * Floating emoji reaction bar. Local taps:
 *   1. Emit an immediate burst for instant feedback.
 *   2. Send a `Reaction` ClientMsg via the active driver — the server
 *      echoes it as a `Reaction` ServerMsg, which lands on every WS in
 *      the room (including the sender, but the local burst's own id key
 *      means the echo doesn't double-render).
 *
 * Inbound reactions from the store render their own bursts in the same
 * shared canvas. New entries get a TTL clean-up so the array can't grow.
 */
export const ReactionBar = ({ disabled, onReact }: ReactionBarProps) => {
  const driver = useSessionStore((s) => s.driver);
  const identity = useSessionStore((s) => s.identity);
  const reactions = useGameStore((s) => s.reactions);
  const [bursts, setBursts] = useState<Burst[]>([]);
  const idRef = useRef(0);
  const seenRef = useRef(new Set<string>());

  const pushBurst = useCallback((id: string, emoji: string): void => {
    if (seenRef.current.has(id)) return;
    seenRef.current.add(id);
    const burst: Burst = {
      id,
      emoji,
      dx: (Math.random() - 0.5) * 60,
      dy: 120 + Math.random() * 80,
      dur: 1200 + Math.random() * 400,
    };
    setBursts((bs) => [...bs, burst]);
    setTimeout(() => {
      setBursts((bs) => bs.filter((b) => b.id !== burst.id));
      seenRef.current.delete(id);
    }, BURST_TTL_MS);
  }, []);

  // Replay incoming reactions. We track each `(playerId, at)` once via
  // `seenRef` so the same Reaction never gets two bursts even if the
  // store re-emits during a snapshot replay.
  useEffect(() => {
    const cutoff = Date.now() - BURST_TTL_MS;
    for (const r of reactions) {
      if (r.at < cutoff) continue;
      pushBurst(`net:${r.playerId}:${r.at}`, r.emoji);
    }
  }, [reactions, pushBurst]);

  const emit = (emoji: string): void => {
    idRef.current += 1;
    pushBurst(`local:${idRef.current}`, emoji);
    if (driver && identity) {
      try {
        driver.send({
          kind: ClientMsgKind.Reaction,
          actorPlayerId: identity.playerId,
          emoji,
        });
      } catch {
        // best-effort — local burst already showing
      }
    }
    onReact?.(emoji);
  };

  return (
    <div className="relative inline-flex">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10">
        <AnimatePresence>
          {bursts.map((b) => (
            <motion.span
              key={b.id}
              initial={{ opacity: 0, y: 0, scale: 0.4, rotate: -12 }}
              animate={{ opacity: [0, 1, 1, 0], y: -b.dy, scale: 1.05, rotate: 8 }}
              exit={{ opacity: 0 }}
              transition={{ duration: b.dur / 1000, ease: [0.2, 0.6, 0.4, 1] }}
              className="absolute left-1/2 text-[28px]"
              style={{
                transform: `translateX(calc(-50% + ${b.dx}px))`,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              {b.emoji}
            </motion.span>
          ))}
        </AnimatePresence>
      </div>

      <div
        className="paper-dossier flex items-center gap-1 rounded-full px-3 py-1.5"
        style={{
          pointerEvents: disabled ? 'none' : 'auto',
          opacity: disabled ? 0.4 : 1,
        }}
      >
        {REACTIONS.map((r) => (
          <ReactionBtn
            key={r.emoji}
            emoji={r.emoji}
            label={r.label}
            color={r.color}
            onTap={() => emit(r.emoji)}
          />
        ))}
      </div>
    </div>
  );
};

const ReactionBtn = ({
  emoji,
  label,
  color,
  onTap,
}: {
  emoji: string;
  label: string;
  color: string;
  onTap: () => void;
}) => {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      type="button"
      aria-label={label}
      onClick={() => {
        setPressed(true);
        setTimeout(() => setPressed(false), 180);
        onTap();
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
          navigator.vibrate?.(10);
        }
      }}
      className="flex h-9 w-9 items-center justify-center rounded-full p-0 text-[22px] leading-none transition-transform"
      style={{
        background: pressed ? color : 'transparent',
        transform: pressed ? 'scale(1.25)' : 'scale(1)',
      }}
    >
      <span style={{ filter: pressed ? 'drop-shadow(0 0 4px rgba(255,255,255,.8))' : 'none' }}>
        {emoji}
      </span>
    </button>
  );
};
